import { type ReactElement, type ReactNode } from 'react'
import {
  isCompanyId,
  isDefaultRoleId,
  isEmpty,
  isNotEmpty,
  isRoleId,
  isTeamId,
  isUserId,
  PERMISSION_DEFINITION,
  type AuditProps,
  type PermissionDefinition,
  type PermissionEntityId,
  type PermissionListItem,
  type PermissionType,
  type WithPermissionEntityId,
} from '@terros/common'
import { Badge, Divider, Group, Stack, Text } from '@mantine/core'
import { Size, SizePrimitives, useTranslation } from '@front/model'
import { TStack } from '../TStack'
import { RoleInfo, TeamInfo, UserInfo } from '../entityInfo'
import { CompanyInfo } from '../CompanyInfo'
import { ModificationStack } from '../auditInfo'
import { stringifyPermissionScope } from './util'
import { type PermissionAllowedScope } from './types'

type Props = {
  permissionInfo: PermissionListItem
  permissionType: PermissionType
}

export const PermissionSource = ({ permissionInfo, permissionType }: Props): ReactNode => {
  const { t } = useTranslation()
  const { primaryScope } = permissionInfo
  const activeOverrides = permissionInfo.allOverrides?.filter((permission) => permission.scope !== 'inherit')
  const showPrimaryScope = isNotEmpty(primaryScope) && isEmpty(activeOverrides)
  const showEmptyItem = isEmpty(primaryScope) && isEmpty(activeOverrides)
  return (
    <Stack>
      <Text className='font-semibold'>{t('Permission Source')}</Text>
      {showEmptyItem && <PermissionItem permissionType={permissionType} entity='none' entityId={undefined} />}
      {showPrimaryScope && (
        <PermissionItem
          permissionType={permissionType}
          {...primaryScope}
          entity={primaryScope.entity}
          entityId={primaryScope.source}
        />
      )}
      {permissionInfo.allOverrides?.map((permission) => (
        <PermissionItem {...permission} entity={permission.scope} isOverride />
      ))}
    </Stack>
  )
}

export const PermissionSourceEntity = ({ entityId }: WithPermissionEntityId): ReactElement => {
  if (isDefaultRoleId(entityId))
    return (
      <Badge variant='light' color='green'>
        {entityId.split('.')[1]}
      </Badge>
    )
  if (isTeamId(entityId)) return <TeamInfo teamId={entityId} linked={false} size='sm' />
  if (isCompanyId(entityId)) return <CompanyInfo companyId={entityId} linked={false} size='sm' />
  if (isRoleId(entityId)) return <RoleInfo roleId={entityId} linked={false} size='sm' />
  if (isUserId(entityId)) return <UserInfo userId={entityId} linked={false} size='sm' />

  return <Badge variant='outline'>{entityId}</Badge>
}

const PermissionItem = ({
  permissionType,
  description,
  entity,
  entityId,
  isOverride,
  ...audit
}: AuditProps & {
  permissionType: PermissionType
  description?: string
  entity: PermissionAllowedScope | 'inherit'
  entityId: PermissionEntityId | undefined
  isOverride?: boolean
}): ReactElement => {
  const { t } = useTranslation()
  return (
    <TStack
      divider={<Divider />}
      p={Size.padding.basic}
      key={permissionType}
      gap='xs'
      className='border-neutral-light rounded-sm border'
    >
      <Group gap={SizePrimitives['1']}>
        <Text className='font-medium'>{getDefinition(entity, permissionType)}</Text>
        <Text fw='bold' className='shrink-0'>
          {entity === 'inherit' ? 'Inherited' : stringifyPermissionScope(entity)}
        </Text>
      </Group>
      {entityId && (
        <Group>
          <Text>{t(getInheritanceLabel(isOverride))}</Text>
          <PermissionSourceEntity entityId={entityId} />
        </Group>
      )}
      {isNotEmpty(audit) && (
        <Stack>
          <Text>{t('History:')}</Text>
          <ModificationStack {...audit} />
        </Stack>
      )}
    </TStack>
  )
}

function getDefinition(scope: PermissionAllowedScope | 'inherit', permissionType: PermissionType): string {
  if (scope === 'none' || scope === 'inherit') return ''
  const definition: PermissionDefinition = PERMISSION_DEFINITION[permissionType]
  return `Allow the user to ${definition.action} ${definition.additionalDescription ?? ''} for: `
}

export function getInheritanceLabel(isOverride: boolean | undefined): string {
  return isOverride ? 'Changed on:' : 'No change from:'
}
